import clsx from 'clsx';

import { Card, Progress } from '@lib/ui';
import { setThisClass } from '@lib/utils';

export type ProjectCardColor = 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error';

export interface ProjectCardProps {
  /** Project name */
  name: string;
  /** Image URL for the project thumbnail */
  image?: string;
  /** Last updated date string */
  updatedAt?: string;
  /** Deadline date string */
  deadline?: string;
  /** Progress percentage (0-100) */
  progress: number;
  /** Whether the progress bar should be animated */
  isActive?: boolean;
  /** Color theme */
  color?: ProjectCardColor;
  /** Additional className */
  className?: string;
  /** Called when the card is clicked */
  onClick?: () => void;
}

export function ProjectCard({
  name,
  image,
  updatedAt,
  deadline,
  progress,
  isActive,
  color = 'primary',
  className,
  onClick,
}: ProjectCardProps) {
  return (
    <Card
      className={clsx(
        'p-3',
        onClick && 'cursor-pointer hover:shadow-md transition-shadow',
        className
      )}
      onClick={onClick}
    >
      <div className="flex items-center gap-3">
        {image ? (
          <img className="size-10 rounded-lg object-cover object-center" src={image} alt={name} />
        ) : (
          <div className="size-10 rounded-lg bg-gray-200 dark:bg-dark-500 flex items-center justify-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="size-5 text-gray-400 dark:text-dark-300"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M2.25 12.75V12A2.25 2.25 0 0 1 4.5 9.75h15A2.25 2.25 0 0 1 21.75 12v.75m-8.69-6.44-2.12-2.12a1.5 1.5 0 0 0-1.061-.44H4.5A2.25 2.25 0 0 0 2.25 6v12a2.25 2.25 0 0 0 2.25 2.25h15A2.25 2.25 0 0 0 21.75 18V9a2.25 2.25 0 0 0-2.25-2.25h-5.379a1.5 1.5 0 0 1-1.06-.44Z"
              />
            </svg>
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex justify-between">
            <p className="truncate font-medium text-gray-800 dark:text-dark-100">{name}</p>
          </div>
          <div className="mt-0.5 flex text-xs text-gray-400 dark:text-dark-300">
            {updatedAt && <p>{updatedAt}</p>}
            {updatedAt && deadline && (
              <div className="mx-2 my-1 hidden w-px bg-gray-200 dark:bg-dark-500 sm:flex"></div>
            )}
            {deadline && <p className="hidden sm:flex">Deadline: {deadline}</p>}
          </div>
        </div>
      </div>
      <p
        className={clsx(
          setThisClass(color),
          '-mt-3 text-end text-xs font-medium text-this dark:text-this-lighter'
        )}
      >
        {progress}%
      </p>

      <Progress
        value={progress}
        color={color}
        isActive={isActive}
        classNames={{
          root: 'mt-2 h-1.5',
        }}
      />
    </Card>
  );
}

ProjectCard.displayName = 'ProjectCard';
